import axios from "axios";

const BASE = `${process.env.REACT_APP_BECCO_API_URL}/api/sfq`;

export const sfqApi = {
  // ── Study ──────────────────────────────────────────────────────────────────
  list:            ()           => axios.get(BASE),
  detail:          (id)         => axios.get(`${BASE}/${id}`),
  // body: { studyCode, studyTitle, reviewerEmployeeNo, craEmployeeNos: [] }
  create:          (body)       => axios.post(BASE, body),
  // Employees who may be assigned to a study, resolved server-side by permission.
  craCandidates:      ()        => axios.get(`${BASE}/cra-candidates`),      // hold SFQ_CRA_EDIT
  reviewerCandidates: ()        => axios.get(`${BASE}/reviewer-candidates`), // hold SFQ_REVIEWER
  ownerCandidates:    ()        => axios.get(`${BASE}/owner-candidates`),    // hold SFQ_STUDY_EDIT (Admin only)
  // reason is required for Send Back (InternalReview → Draft), optional elsewhere.
  updateStatus:    (id, newStatus, reason) => axios.put(`${BASE}/${id}/status`, { newStatus, reason }),
  updateInfo:      (id, body)   => axios.put(`${BASE}/${id}/info`, body),
  updateNarrative: (id, html)   => axios.put(`${BASE}/${id}/narrative`, { html }),
  saveSFQQuestions:(id, questions) => axios.put(`${BASE}/${id}/questions`, { questions }),

  // ── Version Management ───────────────────────────────────────────────────────
  // Publishing takes the study's PM: the CRA requests, the PM approves or rejects.
  requestPublish:       (id)         => axios.post(`${BASE}/${id}/versions/publish`),
  cancelPublishRequest: (id)         => axios.post(`${BASE}/${id}/versions/publish/cancel`),
  approvePublish:       (id)         => axios.post(`${BASE}/${id}/versions/publish/approve`),
  rejectPublish:        (id, reason) => axios.post(`${BASE}/${id}/versions/publish/reject`, { reason }),
  discardDraft:    (id)             => axios.post(`${BASE}/${id}/versions/discard`),
  getVersions:     (id)             => axios.get(`${BASE}/${id}/versions`),
  getVersion:      (id, versionId)  => axios.get(`${BASE}/${id}/versions/${versionId}`),
  // What changed: a version against its predecessor, or the working copy against the
  // latest published version (what approving a pending publish request would apply).
  getVersionDiff:  (id, versionId)  => axios.get(`${BASE}/${id}/versions/${versionId}/diff`),
  getWorkingDiff:  (id)             => axios.get(`${BASE}/${id}/versions/working/diff`),

  // ── Site Directory ─────────────────────────────────────────────────────────
  // Sites already invited in any study the caller may see, deduplicated and newest
  // first — AddSiteModal autofills from these. q searches code/name/country/PI/CRC.
  siteDirectory: (q) => axios.get(`${BASE}/site-directory`, { params: { q: q || undefined } }),

  // ── Site Invitations ───────────────────────────────────────────────────────
  getInvitations:       (sfqId)           => axios.get(`${BASE}/${sfqId}/invitations`),
  addInvitation:        (sfqId, data)     => axios.post(`${BASE}/${sfqId}/invitations`, data),
  // Partial: only the fields sent are touched. Which fields are accepted depends on the
  // invitation's status — Pending takes all of them, a sent one only the contacts, and a
  // submitted one none. The API is the authority and rejects the rest with 422.
  updateInvitation:     (sfqId, invId, data) =>
    axios.put(`${BASE}/${sfqId}/invitations/${invId}`, data),
  // Extends the link the site already holds; unlike a resend it keeps their password.
  // `expiry` is the KST end-of-day instant as an ISO string, as in addInvitation.
  extendInvExpiry:      (sfqId, invId, expiry) =>
    axios.put(`${BASE}/${sfqId}/invitations/${invId}/expiry`, { expiry }),
  // Closes a link that has already been mailed — for an invitation sent to the wrong
  // address, or saying the wrong thing. The mail itself cannot be recalled; this pulls the
  // expiry back to now so the site's URL answers 410. The password is left alone, so
  // moving the expiry back out (Edit) reopens the same mail and a resend replaces it.
  // `reason` is optional and is kept only in the audit trail.
  revokeInvitation:     (sfqId, invId, reason) =>
    axios.put(`${BASE}/${sfqId}/invitations/${invId}/revoke`, { reason }),
  // mail = { subject, bodyHtml, lang, includeCrc } as composed in SendInvitationModal.
  // {{password}} and {{publicUrl}} are still unresolved — the API substitutes them at
  // send time. includeCrc=false drops the CRC from the recipients; the PI always gets it.
  sendInvitation:       (sfqId, invId, mail) =>
    axios.put(`${BASE}/${sfqId}/invitations/${invId}/send`, mail),
  getInvitationDetail:  (sfqId, invId)    => axios.get(`${BASE}/${sfqId}/invitations/${invId}`),
  // `reason` is required when newStatus walks a rollback edge (Finalized→Reviewed,
  // Reviewed→Submitted) and ignored going forward.
  updateInvStatus:      (sfqId, invId, newStatus, reason) =>
    axios.put(`${BASE}/${sfqId}/invitations/${invId}/status`, { newStatus, reason }),
  requestRiskOverride:  (sfqId, invId, overrideRisk, reason) =>
    axios.put(`${BASE}/${sfqId}/invitations/${invId}/risk-override`, { overrideRisk, reason }),
  approveRiskOverride:  (sfqId, invId) =>
    axios.put(`${BASE}/${sfqId}/invitations/${invId}/risk-override/approve`),
  // The other exit from a pending request: the requester withdraws their own, the owner
  // rejects anyone else's. `reason` is required only for the owner's rejection.
  rejectRiskOverride:   (sfqId, invId, reason) =>
    axios.put(`${BASE}/${sfqId}/invitations/${invId}/risk-override/reject`, { reason }),

  // ── CRA Assessment ─────────────────────────────────────────────────────────
  saveCRAAssessment: (sfqId, invId, type, data) =>
    axios.put(`${BASE}/${sfqId}/invitations/${invId}/cra-assessment`, { type, ...data }),
  saveCRAComment: (sfqId, invId, questionKey, commentText) =>
    axios.put(`${BASE}/${sfqId}/invitations/${invId}/answers/${questionKey}/comment`, { commentText }),

  // ── PDF Export ─────────────────────────────────────────────────────────────
  exportPDF: (sfqId, invId) =>
    axios.get(`${BASE}/${sfqId}/invitations/${invId}/pdf`, { responseType: "blob" }),

  // ── Word Export ────────────────────────────────────────────────────────────
  exportWord: (sfqId, invId) =>
    axios.get(`${BASE}/${sfqId}/invitations/${invId}/word`, { responseType: "blob" }),

  // ── Email Logs ─────────────────────────────────────────────────────────────
  getEmailLogs: (sfqId, invId) => axios.get(`${BASE}/${sfqId}/invitations/${invId}/email-logs`),

  // The last message composed for this site, with {{var}} placeholders still in place, so
  // a resend reopens what was actually sent instead of the study template. Comes back null
  // for a site that has never been sent one (or was, before the message was captured).
  getLastSentMail: (sfqId, invId) =>
    axios.get(`${BASE}/${sfqId}/invitations/${invId}/last-mail`),

  // ── Invitation Email Template (per study, ko/en) ───────────────────────────
  // A language with no saved row comes back null → the frontend default is used.
  getEmailTemplate:  (sfqId)       => axios.get(`${BASE}/${sfqId}/email-template`),
  saveEmailTemplate: (sfqId, data) => axios.put(`${BASE}/${sfqId}/email-template`, data),

  // ── Reminder Mail (per study) ──────────────────────────────────────────────
  // 답변이 없는 사이트에 며칠마다 나가는 알림 메일. 템플릿은 초대와 같은 모양이고
  // (data = { lang, subject, bodyHtml }), 같은 폴백 규칙을 따른다 — 저장된 행이 없으면
  // reminderTemplates.js의 기본값을 쓴다.
  //
  // 본문에 {{password}}를 넣으면 API가 거부한다. 알림은 비밀번호를 재발급하지 않으므로
  // 서버가 치환할 평문 자체를 갖고 있지 않다.
  getReminderTemplate:  (sfqId)       => axios.get(`${BASE}/${sfqId}/reminder-template`),
  saveReminderTemplate: (sfqId, data) => axios.put(`${BASE}/${sfqId}/reminder-template`, data),

  // 발송 주기. data = { enabled, intervalDays, maxCount }.
  getReminderSettings:  (sfqId)       => axios.get(`${BASE}/${sfqId}/reminder-settings`),
  saveReminderSettings: (sfqId, data) => axios.put(`${BASE}/${sfqId}/reminder-settings`, data),

  // 한 사이트에 지금 알림을 보낸다. mail = { subject, bodyHtml, lang, includeCrc },
  // {{publicUrl}}은 아직 치환되지 않은 채로 간다 (sendInvitation과 같은 규칙).
  sendReminder: (sfqId, invId, mail) =>
    axios.put(`${BASE}/${sfqId}/invitations/${invId}/reminder`, mail),

  // 지난 알림 본문. getLastSentMail의 알림판 — 같은 라우트를 mailType으로 갈라 쓴다.
  getLastReminderMail: (sfqId, invId) =>
    axios.get(`${BASE}/${sfqId}/invitations/${invId}/last-mail`, { params: { type: "Reminder" } }),

  // ── 개인정보 동의문 Template (per study, ko/en) ─────────────────────────────
  // Same fallback rule as the mail template. data = { lang, title, bodyHtml }.
  getConsentTemplate:  (sfqId)       => axios.get(`${BASE}/${sfqId}/consent-template`),
  saveConsentTemplate: (sfqId, data) => axios.put(`${BASE}/${sfqId}/consent-template`, data),

  // ── Invitation Email Images ────────────────────────────────────────────────
  // The composer uploads a dropped/pasted image and puts emailAssetUrl() in the body's
  // <img src>. That URL is what gets stored; the API swaps it for a CID inline attachment
  // when the mail actually goes out, since the recipient cannot reach this server.
  uploadEmailAsset: (sfqId, file) => {
    const form = new FormData();
    form.append("file", file);
    return axios.post(`${BASE}/${sfqId}/email-asset`, form);
  },
  emailAssetUrl: (sfqId, assetId) => `${BASE}/${sfqId}/email-asset/${assetId}`,

  // ── Audit Trail ────────────────────────────────────────────────────────────
  getAuditTrail: (id) => axios.get(`${BASE}/${id}/audit`),

  // ── Narrative Templates ────────────────────────────────────────────────────
  // Two scopes: "default" (org-wide, SFQ_ADMIN only) and "user" (private). A list
  // call returns both — the defaults plus the caller's own. body = { name, scope, html }.
  listNarrativeTemplates:  ()          => axios.get(`${BASE}/narrative-templates`),
  getNarrativeTemplate:    (tplId)     => axios.get(`${BASE}/narrative-templates/${tplId}`),
  createNarrativeTemplate: (body)      => axios.post(`${BASE}/narrative-templates`, body),
  // name and html may be sent independently — the editor debounces html, commits name on blur.
  updateNarrativeTemplate: (tplId, body) => axios.put(`${BASE}/narrative-templates/${tplId}`, body),
  deleteNarrativeTemplate: (tplId)     => axios.delete(`${BASE}/narrative-templates/${tplId}`),

  // ── Question Templates ─────────────────────────────────────────────────────
  // Same two scopes. A template is a named set; its questions are saved as a whole.
  listQuestionTemplates:  ()      => axios.get(`${BASE}/question-templates`),
  getQuestionTemplate:    (tplId) => axios.get(`${BASE}/question-templates/${tplId}`),
  createQuestionTemplate: (body)  => axios.post(`${BASE}/question-templates`, body),
  updateQuestionTemplate: (tplId, body) => axios.put(`${BASE}/question-templates/${tplId}`, body),
  saveQuestionTemplateQuestions: (tplId, questions) =>
    axios.put(`${BASE}/question-templates/${tplId}/questions`, { questions }),
  deleteQuestionTemplate: (tplId) => axios.delete(`${BASE}/question-templates/${tplId}`),

  // ── Prior Content ──────────────────────────────────────────────────────────
  // Every published version of the studies the caller created or is PM of, offered
  // in the template picker alongside the templates themselves.
  listPriorVersions: ()          => axios.get(`${BASE}/prior-content`),
  getPriorVersion:   (versionId) => axios.get(`${BASE}/prior-content/${versionId}`),
};
